import { TILE_SIZE } from '@veriverse/shared';
import { findObjectLayer, objectFootprint, objectType, spriteBaseRows, tileId } from './model.js';
import { isBlockingObject, readProperties } from './properties.js';
import { SPRITE_MAX_TILES, SPRITE_TYPE, SPRITES_LAYER } from './rules.js';
import { type TiledMap, type TiledObject } from './types.js';

/**
 * Sprite objects (ADR-0022). A sprite is a block of tiles from the map's tilesets, drawn as one
 * picture and depth-sorted against characters by its bottom row, so a character can walk behind
 * a tall bookshelf and in front of it. Only its bottom `base_rows` rows stand on the floor and
 * may block movement.
 *
 * Stored as a rectangle object of type `sprite` on the `sprites` layer; the tile ids are the
 * string property `tiles`, rows separated by `;` and ids by `,` (0 = transparent).
 */
export interface Sprite {
  readonly id: number;
  readonly name: string;
  /** Top-left tile. */
  readonly x: number;
  readonly y: number;
  /** Size in tiles. */
  readonly width: number;
  readonly height: number;
  /** Tile ids, top row first; every row has `width` entries. */
  readonly rows: readonly (readonly number[])[];
  readonly baseRows: number;
  readonly blocking: boolean;
  readonly visible: boolean;
  /** Row the sprite is sorted by: its bottom row (see `spriteSortRow`). */
  readonly sortRow: number;
}

/** Encodes tile rows for the `tiles` property. */
export function encodeSpriteTiles(rows: readonly (readonly number[])[]): string {
  return rows.map((row) => row.join(',')).join(';');
}

/**
 * Decodes the `tiles` property. Returns `undefined` unless it is a non-empty rectangle of
 * non-negative integers.
 */
export function decodeSpriteTiles(text: unknown): number[][] | undefined {
  if (typeof text !== 'string' || text.trim() === '') return undefined;
  const rows = text.split(';').map((row) => row.split(',').map((cell) => Number(cell.trim())));
  const width = rows[0]?.length ?? 0;
  const valid = rows.every(
    (row) =>
      row.length === width &&
      row.every((gid) => Number.isInteger(gid) && gid >= 0 && gid <= 0xffffffff),
  );
  return valid && width > 0 ? rows : undefined;
}

/**
 * Characters and sprites are drawn in order of the row they stand on. A sprite sorts by its
 * bottom row; a character on that same row is drawn in front of it.
 */
export function spriteSortRow(object: TiledObject): number {
  const { y, h } = objectFootprint(object);
  return y + h - 1;
}

/** Sprites of a map, sorted back to front. Sprites with unreadable `tiles` are skipped. */
export function getSprites(map: TiledMap): Sprite[] {
  const sprites: Sprite[] = [];
  for (const object of findObjectLayer(map, SPRITES_LAYER)?.objects ?? []) {
    if (objectType(object) !== SPRITE_TYPE) continue;
    const rows = decodeSpriteTiles(readProperties(object).tiles);
    const bounds = objectFootprint(object);
    if (!rows || rows.length !== bounds.h || rows[0]?.length !== bounds.w) continue;
    sprites.push({
      id: object.id,
      name: object.name,
      x: bounds.x,
      y: bounds.y,
      width: bounds.w,
      height: bounds.h,
      rows,
      baseRows: spriteBaseRows(object),
      blocking: isBlockingObject(object, SPRITE_TYPE),
      visible: object.visible,
      sortRow: spriteSortRow(object),
    });
  }
  // Stable: sprites on the same row keep their layer order.
  return sprites.sort((a, b) => a.sortRow - b.sortRow);
}

/** Problems with a sprite's tile data, as readable messages (empty when valid). */
export function checkSpriteTiles(object: TiledObject): string[] {
  const rows = decodeSpriteTiles(readProperties(object).tiles);
  if (!rows) return ['"tiles" must be rows of tile ids like "1,2;3,4"'];
  const { w, h } = objectFootprint(object);
  const problems: string[] = [];
  if (rows.length !== h || rows[0]?.length !== w)
    problems.push(`"tiles" is ${rows[0]?.length ?? 0}x${rows.length} but the sprite is ${w}x${h}`);
  if (w > SPRITE_MAX_TILES || h > SPRITE_MAX_TILES)
    problems.push(`sprites can be at most ${SPRITE_MAX_TILES}x${SPRITE_MAX_TILES} tiles`);
  if (rows.every((row) => row.every((gid) => tileId(gid) === 0)))
    problems.push('the sprite has no visible tiles');
  return problems;
}

/** A new sprite showing `rows` (top row first) with its top-left corner on tile `(tx, ty)`. */
export function newSprite(
  rows: readonly (readonly number[])[],
  tx: number,
  ty: number,
  options: { name?: string; baseRows?: number; blocking?: boolean } = {},
): Omit<TiledObject, 'id'> {
  const height = rows.length;
  const width = Math.max(0, ...rows.map((row) => row.length));
  const padded = rows.map((row) => [...row, ...new Array<number>(width - row.length).fill(0)]);
  const baseRows = Math.min(Math.max(options.baseRows ?? 1, 1), Math.max(height, 1));
  return {
    name: options.name ?? 'sprite',
    type: SPRITE_TYPE,
    x: tx * TILE_SIZE,
    y: ty * TILE_SIZE,
    width: width * TILE_SIZE,
    height: height * TILE_SIZE,
    rotation: 0,
    visible: true,
    properties: [
      { name: 'tiles', type: 'string', value: encodeSpriteTiles(padded) },
      { name: 'base_rows', type: 'int', value: baseRows },
      { name: 'blocking', type: 'bool', value: options.blocking ?? true },
    ],
  };
}
